/**
 * Terms of Service Page
 *
 * Displays terms and conditions for using CPR Training Direct services.
 * BRUTALIST STREETWEAR DESIGN SYSTEM
 */

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function Terms() {
  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Terms of Service | CPR Training Direct</title>
        <meta name="title" content="Terms of Service | CPR Training Direct" />
        <meta
          name="description"
          content="Terms and conditions for using CPR Training Direct services. Read our service agreement and policies."
        />
        <meta name="author" content="CPR Training Direct" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://cprtrainingdirect.com/terms" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cprtrainingdirect.com/terms" />
        <meta property="og:title" content="Terms of Service | CPR Training Direct" />
        <meta property="og:description" content="Terms and conditions for using CPR Training Direct services." />
        <meta property="og:image" content="https://cprtrainingdirect.com/og-image.jpg" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Terms of Service | CPR Training Direct" />
      </Helmet>

      {/* Hero Section - Brutalist */}
      <section className="bg-indigo_dye-500 text-white py-16 md:py-20 px-6">
        <div className="container mx-auto text-center">
          <h1
            data-aos="fade-up"
            className="font-display text-5xl md:text-6xl lg:text-7xl font-bold uppercase tracking-wider mb-4"
          >
            Terms of Service
          </h1>
          <p
            data-aos="fade-up"
            data-aos-delay="100"
            className="font-mono text-lg text-indigo_dye-200 uppercase tracking-wide"
          >
            Last Updated: October 30, 2024
          </p>
        </div>
      </section>

      {/* Page Container */}
      <div className="bg-white min-h-screen py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Back Link */}
            <div data-aos="fade-up" className="mb-8">
              <Link
                to="/"
                className="inline-flex items-center gap-2 font-mono text-indigo_dye-600 hover:text-amaranth_purple-600 font-bold uppercase tracking-wide transition-colors"
              >
                ← Back to Home
              </Link>
            </div>

            {/* Content */}
            <div data-aos="fade-up" data-aos-delay="100" className="border-4 border-gray-400 shadow-[8px_8px_0px_0px_rgba(2,5,18,1)] p-8 md:p-12">
              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-0 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Acceptance of Terms
              </h2>
              <p className="font-body text-gray-700 mb-6 leading-relaxed">
                By accessing and using the CPR Training Direct website and services, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
              </p>

              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-8 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Course Registration
              </h2>
              <p className="font-body text-gray-700 mb-4 leading-relaxed">
                When you register for a CPR training course:
              </p>
              <ul className="list-none pl-0 mb-6 space-y-2">
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> You agree to provide accurate registration information</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Payment is required to secure your enrollment</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Course fees are non-refundable except per cancellation policy</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> You are responsible for attending your scheduled class</li>
              </ul>

              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-8 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Cancellation Policy
              </h2>
              <div className="bg-amaranth_purple-50 border-4 border-amaranth_purple-200 p-4 mb-6">
                <ul className="list-none pl-0 space-y-2">
                  <li className="font-body text-gray-700"><span className="font-mono font-bold">48+ hours:</span> Full refund or free rescheduling</li>
                  <li className="font-body text-gray-700"><span className="font-mono font-bold">24-48 hours:</span> 50% refund or reschedule with $25 fee</li>
                  <li className="font-body text-gray-700"><span className="font-mono font-bold">&lt;24 hours:</span> No refund; reschedule with $50 fee</li>
                  <li className="font-body text-gray-700"><span className="font-mono font-bold">No-show:</span> No refund or credit</li>
                </ul>
              </div>

              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-8 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Certification Requirements
              </h2>
              <p className="font-body text-gray-700 mb-4 leading-relaxed">
                To receive certification, students must:
              </p>
              <ul className="list-none pl-0 mb-6 space-y-2">
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Attend the full duration of the course</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Actively participate in all skill practice sessions</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Demonstrate competency in required skills</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Pass any required written assessments</li>
              </ul>

              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-8 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Certification Validity
              </h2>
              <p className="font-body text-gray-700 mb-6 leading-relaxed">
                CPR certifications are valid for 2 years from issuance. You are responsible for tracking expiration dates and registering for renewal courses as needed.
              </p>

              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-8 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Limitation of Liability
              </h2>
              <p className="font-body text-gray-700 mb-6 leading-relaxed">
                Our liability is limited to the amount paid for course registration. We are not liable for outcomes of CPR performed, employment opportunities, or indirect damages.
              </p>

              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-8 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Contact Us
              </h2>
              <p className="font-body text-gray-700 mb-4 leading-relaxed">
                Questions about these Terms of Service? Contact us:
              </p>
              <div className="bg-indigo_dye-50 border-4 border-indigo_dye-200 p-4 mb-4">
                <p className="font-body text-gray-700">
                  <strong className="font-mono uppercase">CPR Training Direct</strong><br />
                  Contact us via our <Link to="/contact" className="text-indigo_dye-600 hover:text-amaranth_purple-600 font-semibold">contact page</Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
